/**
 * "Buy it now" express checkout.
 *
 * Adds the selected variant to the cart using the product form the button
 * sits in, then opens Shiprocket checkout, the same checkout the store's
 * cart drawer and cart page use for every shopper. Shiprocket's script is
 * loaded outside this theme, so this file drives it the way the drawer's
 * button does rather than calling into it. Only if that script has not
 * loaded does it fall back to the native checkout button, so the shopper
 * still reaches a checkout rather than a dead end.
 *
 * Each line it adds is tagged with a private `_buy_it_now` property, which
 * does two things. Shopify keeps a tagged line separate from an untagged
 * one for the same variant, so a shopper's own add-to-cart copy of the
 * product is never touched. And it lets a later click find what earlier
 * express checkouts left behind when the shopper backed out, so those
 * lines are cleared instead of piling up in the next checkout. An ordinary
 * add to cart clears them the same way (product-form.js, via the
 * `data-buy-it-now-key` marker the cart drawer puts on tagged lines), so
 * backing out of Buy it now and then adding the product does not show it
 * twice in the cart.
 *
 * Add-to-cart lines are deliberately left alone, which means a shopper who
 * has items in their cart will still see them at checkout alongside the
 * Buy it now product. Only Shopify's native dynamic checkout button can
 * check out a single product while leaving the cart completely untouched.
 *
 * Publishes the same `cartUpdate` event as product-form.js so add-to-cart
 * analytics (cart-add-tracking.js) and abandoned cart recovery
 * (cart-recovery.js) keep seeing the sale. Both send with
 * navigator.sendBeacon, which survives the navigation that follows.
 *
 * The button's disabled state mirrors add to cart, which custom-product.js
 * already toggles as the shopper switches to sold-out variants.
 */
(() => {
	// The snippet renders in several sections, so guard against a second
	// script tag wiring up a duplicate click handler.
	if (window.buyItNowInitialized) return;
	window.buyItNowInitialized = true;

	const BUTTON_SELECTOR = '[data-buy-it-now]';
	const ADD_TO_CART_SELECTOR = '.product_submit_button';

	// Leading underscore keeps it out of the cart, checkout, and order
	// confirmation the shopper sees. It stays visible to you in the admin.
	const LINE_PROPERTY = '_buy_it_now';

	const NATIVE_CHECKOUT_FORM_ID = 'BuyItNowNativeCheckout';

	// How long the button stays locked after the shopper is handed to a
	// checkout provider: long enough to cover the provider reading the cart
	// and opening its window, so a double-click cannot add the product
	// twice; short enough that closing that window leaves a live button.
	const HANDOFF_GRACE_MS = 1500;

	function setLoading(button, isLoading) {
		button.classList.toggle('loading', isLoading);
		if (isLoading) {
			button.setAttribute('aria-disabled', 'true');
		} else {
			button.removeAttribute('aria-disabled');
		}
	}

	function showError(productForm, message) {
		if (!productForm || productForm.dataset.hideErrors === 'true') return;

		const wrapper = productForm.querySelector(
			'.product-form__error-message-wrapper',
		);
		if (!wrapper) return;

		const text = wrapper.querySelector('.product-form__error-message');
		if (text) text.textContent = message || window.cartStrings?.error || '';
		wrapper.toggleAttribute('hidden', !message);
	}

	/** Line item keys a previous Buy it now click put in the cart. */
	async function previousExpressLines() {
		const response = await fetch(`${window.routes.cart_url}.js`, {
			headers: { Accept: 'application/json' },
			cache: 'no-store',
		});
		const cart = await response.json();
		return (cart.items || [])
			.filter((line) => line.properties?.[LINE_PROPERTY])
			.map((line) => line.key);
	}

	/**
	 * Clears the given lines, except the one the shopper just added. A
	 * repeat of the same variant merges into the line that was already
	 * tagged, so that one is reset to the quantity chosen this time rather
	 * than removed.
	 */
	async function clearPreviousExpressLines(keys, addedKey, quantity) {
		const updates = {};
		for (const key of keys) {
			updates[key] = key === addedKey ? quantity : 0;
		}
		if (!Object.keys(updates).length) return;

		await fetch(window.routes.cart_update_url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify({ updates }),
		});
	}

	/**
	 * A hidden copy of the store's native checkout control, used only when
	 * Shiprocket's script is unavailable. Razorpay Magic Checkout watches the
	 * whole document for clicks on `input[name="checkout"]` and opens its own
	 * checkout from them; when it is not running, or hands the cart back, the
	 * form simply posts to Shopify checkout. It stays in the page because
	 * Razorpay re-clicks the original control to fall back.
	 *
	 * An input rather than a button on purpose: for shoppers in India's time
	 * zone Shiprocket swaps every `button[name="checkout"]` on the page for
	 * its own, which would turn this fallback into a second Shiprocket button.
	 */
	function nativeCheckoutControl() {
		let form = document.getElementById(NATIVE_CHECKOUT_FORM_ID);
		if (!form) {
			form = document.createElement('form');
			form.id = NATIVE_CHECKOUT_FORM_ID;
			form.method = 'post';
			form.setAttribute('action', window.routes.cart_url);
			form.hidden = true;
			form.setAttribute('aria-hidden', 'true');

			const control = document.createElement('input');
			control.type = 'submit';
			control.name = 'checkout';
			control.tabIndex = -1;
			form.appendChild(control);
			document.body.appendChild(form);
		}
		return form.elements.namedItem('checkout');
	}

	/**
	 * Sends the prepared cart to Shiprocket checkout, exactly as the cart
	 * drawer's button does. Shiprocket reads the cart itself and has its own
	 * fallback to Shopify checkout; the native control here only covers the
	 * case where its script never loaded.
	 */
	function startCheckout(event) {
		const shiprocket = window.shiprocketCheckoutEvents;
		if (typeof shiprocket?.buyCart === 'function') {
			shiprocket.buyCart(event);
			return;
		}
		nativeCheckoutControl().click();
	}

	async function onClick(button, event) {
		if (button.disabled || button.getAttribute('aria-disabled') === 'true') {
			return;
		}

		const form = button.closest('form');
		if (!form) return;
		const productForm = button.closest('product-form');

		// product-form.js enables this on boot; do it here too so a slow
		// script load can't strip the variant out of the payload.
		const variantInput = form.querySelector('[name="id"]');
		if (variantInput) variantInput.disabled = false;

		// The form carries novalidate for the AJAX add to cart, so required
		// fields such as the gift card recipient need an explicit check.
		if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
			return;
		}

		showError(productForm, false);
		setLoading(button, true);

		try {
			// Read before adding, so the new line is not in the list of
			// leftovers to clear.
			const stale = await previousExpressLines().catch(() => []);

			const formData = new FormData(form);
			formData.append(`properties[${LINE_PROPERTY}]`, 'true');
			const quantity = Number(formData.get('quantity')) || 1;

			const response = await fetch(window.routes.cart_add_url, {
				method: 'POST',
				headers: {
					Accept: 'application/javascript',
					'X-Requested-With': 'XMLHttpRequest',
				},
				body: formData,
			});
			const cartData = await response.json();

			if (cartData.status) {
				showError(productForm, cartData.description || cartData.message);
				setLoading(button, false);
				return;
			}

			if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
				publish(PUB_SUB_EVENTS.cartUpdate, {
					source: 'product-form',
					productVariantId: formData.get('id'),
					cartData,
				});
			}

			// Without a key for the new line there is no way to tell it apart
			// from the leftovers, so leave the cart alone rather than risk
			// clearing the item being bought.
			const addedKey = cartData.key || cartData.items?.[0]?.key;
			if (stale.length && typeof addedKey === 'string') {
				// Must finish before checkout loads, or it reads the old cart.
				await clearPreviousExpressLines(stale, addedKey, quantity).catch(
					(error) => console.error(error),
				);
			}

			startCheckout(event);
			// The provider's loader or window takes over from here; the
			// button only needs to stay locked until it has.
			setTimeout(() => setLoading(button, false), HANDOFF_GRACE_MS);
		} catch (error) {
			console.error(error);
			showError(productForm, window.cartStrings?.error);
			setLoading(button, false);
		}
	}

	/**
	 * Keep "Buy it now" unavailable whenever add to cart is, without having
	 * to hook every code path that disables it.
	 */
	function mirrorAddToCartState(button) {
		if (button.dataset.buyItNowReady === 'true') return;
		button.dataset.buyItNowReady = 'true';

		const addToCart = button
			.closest('form')
			?.querySelector(ADD_TO_CART_SELECTOR);
		if (!addToCart) return;

		const sync = () => {
			button.disabled = addToCart.disabled;
		};
		sync();
		new MutationObserver(sync).observe(addToCart, {
			attributes: true,
			attributeFilter: ['disabled'],
		});
	}

	function init(root = document) {
		root.querySelectorAll(BUTTON_SELECTOR).forEach(mirrorAddToCartState);
	}

	document.addEventListener('click', (event) => {
		const button = event.target.closest?.(BUTTON_SELECTOR);
		if (button) onClick(button, event);
	});

	// Section rendering in the theme editor replaces the buttons wholesale.
	document.addEventListener('shopify:section:load', (event) => {
		init(event.target);
	});

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => init());
	} else {
		init();
	}
})();
